import {useState, useEffect, useCallback} from 'react';
import ReactDOM from 'react-dom';
import {InfoSvg} from '@/icons';
import Icon, {IconSize} from '@/components/Icon';

import styles from './index.module.scss';

export interface ToastProps {
    content: string | React.ReactNode;
    duration?: number;
    onClose?: () => void;
    id?: string;
    count?: number;
    onClickDownCount?: () => void;
}

export interface ToastOptions {
    content: string | React.ReactNode;
    duration: number;
    onClose: () => void;
    id: string;
    count: number;
    stop: boolean;
}

interface ContentProps {
    content?: React.ReactNode;
    svg: React.FunctionComponent;
}

function CustomContent(props: ContentProps) {
    const {svg, content} = props;
    return (
        <>
            <Icon svg={svg} size={IconSize.LG} />
            {content}
        </>
    );
}

let toastList: any = [];

function Toast(props: ToastProps) {
    const {content, id, count, onClickDownCount} = props;

    const onMouseEnter = useCallback(() => {
        toastList.some((item: ToastOptions) => {
            if (item.id === id) {
                item.stop = true;
            }

            return item.id === id;
        });
    }, [id]);

    const onMouseLeave = useCallback(() => {
        toastList.some((item: ToastOptions) => {
            if (item.id === id) {
                item.stop = false;
            }

            return item.id === id;
        });
    }, [id]);

    return (
        <div className={styles.toast} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
            {content}
            <span className={styles.count} onClick={onClickDownCount}>
                {' '}
                ({count}s)
            </span>
        </div>
    );
}

let container: any = null;
// eslint-disable-next-line @typescript-eslint/no-empty-function
let forceUpdate: () => void = () => {};

function destroy() {
    ReactDOM.unmountComponentAtNode(container);
    document.body.removeChild(container);
    container = null;
}

function ToastContainer() {
    const [, setCount] = useState(0);
    forceUpdate = useCallback(() => {
        setCount(val => val + 1);
    }, [setCount]);

    useEffect(() => {
        const timer = window.setInterval(() => {
            toastList.forEach((item: ToastOptions) => {
                if (!item.stop) {
                    item.count--;
                }

                if (item.count <= 0) {
                    item.onClose();
                }
            });

            toastList = toastList.filter((item: ToastOptions) => item.count > 0 || item.stop);
            forceUpdate();
        }, 1000);

        return () => {
            window.clearInterval(timer);
        };
    }, []);

    if (toastList.length === 0) {
        destroy();
    }

    return (
        <div className={styles.container}>
            <div className={styles['toast-list']}>
                {toastList.map((item: ToastOptions) => (
                    <Toast key={item.id} {...item} />
                ))}
            </div>
        </div>
    );
}

function render() {
    container = container || document.createElement('div');
    document.body.appendChild(container);
    ReactDOM.render(<ToastContainer />, container);
}

function toast(props: ToastProps) {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    const {content, duration = 5000, onClose = () => {}, id = Date.now(), onClickDownCount = () => {}} = props;

    const toastOptions = {
        id,
        duration,
        content: <CustomContent content={content} svg={InfoSvg} />,
        onClose: onClose,
        count: Math.round(duration / 1000),
        onClickDownCount,
    };

    toastList.push(toastOptions);

    if (!container) {
        render();
    }

    return {
        remove() {
            toastList = toastList.filter((item: ToastOptions) => item.id !== id);
            forceUpdate();
        },
    };
}

export default toast;
